import { lazy, Suspense } from 'react';
import { useRoutes, Navigate } from 'react-router-dom';
import { useNavigate, type NavigateFunction } from 'react-router-dom';
import { useEffect } from 'react';
import ProtectedRoute from '@/components/ProtectedRoute';

const Home = lazy(() => import('@/pages/home/page'));
const Login = lazy(() => import('@/pages/auth/Login'));
const Register = lazy(() => import('@/pages/auth/Register'));
const ForgotPassword = lazy(() => import('@/pages/auth/ForgotPassword'));
const ResetPassword = lazy(() => import('@/pages/auth/ResetPassword'));
const Explore = lazy(() => import('@/pages/customer/Explore'));
const Wholesale = lazy(() => import('@/pages/customer/Wholesale'));
const ProductDetail = lazy(() => import('@/pages/customer/ProductDetail'));
const Order = lazy(() => import('@/pages/customer/Order'));
const OrderTracking = lazy(() => import('@/pages/customer/OrderTracking'));
const OrderHistory = lazy(() => import('@/pages/customer/OrderHistory'));
const Messages = lazy(() => import('@/pages/customer/Messages'));
const Conversation = lazy(() => import('@/pages/customer/Conversation'));
const Sourcing = lazy(() => import('@/pages/customer/Sourcing'));
const Notifications = lazy(() => import('@/pages/customer/Notifications'));
const Profile = lazy(() => import('@/pages/customer/Profile'));
const Favorites = lazy(() => import('@/pages/customer/Favorites'));
const AdminDashboard = lazy(() => import('@/pages/admin/Dashboard'));
const AdminCategories = lazy(() => import('@/pages/admin/AdminCategories'));
const AdminProducts = lazy(() => import('@/pages/admin/Products'));
const AdminProductNew = lazy(() => import('@/pages/admin/ProductNew'));
const AdminProductEdit = lazy(() => import('@/pages/admin/ProductEdit'));
const AdminOrders = lazy(() => import('@/pages/admin/Orders'));
const AdminOrderDetail = lazy(() => import('@/pages/admin/OrderDetail'));
const AdminMessages = lazy(() => import('@/pages/admin/Messages'));
const AdminSourcing = lazy(() => import('@/pages/admin/Sourcing'));
const AdminNotifications = lazy(() => import('@/pages/admin/Notifications'));
const AdminReports = lazy(() => import('@/pages/admin/Reports'));
const AdminGroupages = lazy(() => import('@/pages/admin/AdminGroupages'));
const AdminGroupageDetail = lazy(() => import('@/pages/admin/AdminGroupageDetail'));
const SuperAdminDashboard = lazy(() => import('@/pages/super-admin/Dashboard'));
const SuperAdminUsers = lazy(() => import('@/pages/super-admin/Users'));
const SuperAdminReports = lazy(() => import('@/pages/super-admin/Reports'));
const SuperAdminSettings = lazy(() => import('@/pages/super-admin/Settings'));
const LegalNotice = lazy(() => import('@/pages/LegalNotice'));
const NotFound = lazy(() => import('@/pages/NotFound'));

function PageLoader() {
  return (
    <div className="flex items-center justify-center h-screen">
      <div className="w-8 h-8 border-2 border-accent-500 border-t-transparent rounded-full animate-spin" />
    </div>
  );
}

const routes = [
  { path: '/', element: <Home /> },
  { path: '/connexion', element: <Login /> },
  { path: '/inscription', element: <Register /> },
  { path: '/mot-de-passe-oublie', element: <ForgotPassword /> },
  { path: '/reinitialiser-mot-de-passe', element: <ResetPassword /> },
  { path: '/mentions-legales', element: <LegalNotice /> },
  { path: '/explore', element: <ProtectedRoute><Explore /></ProtectedRoute> },
  { path: '/gros', element: <ProtectedRoute allowedRoles={['customer']}><Wholesale /></ProtectedRoute> },
  { path: '/product/:id', element: <ProtectedRoute><ProductDetail /></ProtectedRoute> },
  { path: '/order', element: <ProtectedRoute allowedRoles={['customer']}><Order /></ProtectedRoute> },
  { path: '/order/history', element: <ProtectedRoute allowedRoles={['customer']}><OrderHistory /></ProtectedRoute> },
  { path: '/order/tracking/:id', element: <ProtectedRoute allowedRoles={['customer']}><OrderTracking /></ProtectedRoute> },
  { path: '/messages', element: <ProtectedRoute><Messages /></ProtectedRoute> },
  { path: '/messages/:id', element: <ProtectedRoute><Conversation /></ProtectedRoute> },
  { path: '/sourcing', element: <ProtectedRoute allowedRoles={['customer']}><Sourcing /></ProtectedRoute> },
  { path: '/notifications', element: <ProtectedRoute><Notifications /></ProtectedRoute> },
  { path: '/profile', element: <ProtectedRoute><Profile /></ProtectedRoute> },
  { path: '/favorites', element: <ProtectedRoute allowedRoles={['customer']}><Favorites /></ProtectedRoute> },
  { path: '/admin', element: <ProtectedRoute allowedRoles={['admin', 'super_admin']}><AdminDashboard /></ProtectedRoute> },
  { path: '/admin/categories', element: <ProtectedRoute allowedRoles={['admin', 'super_admin']}><AdminCategories /></ProtectedRoute> },
  { path: '/admin/products', element: <ProtectedRoute allowedRoles={['admin', 'super_admin']}><AdminProducts /></ProtectedRoute> },
  { path: '/admin/products/new', element: <ProtectedRoute allowedRoles={['admin', 'super_admin']}><AdminProductNew /></ProtectedRoute> },
  { path: '/admin/products/:id/edit', element: <ProtectedRoute allowedRoles={['admin', 'super_admin']}><AdminProductEdit /></ProtectedRoute> },
  { path: '/admin/orders', element: <ProtectedRoute allowedRoles={['admin', 'super_admin']}><AdminOrders /></ProtectedRoute> },
  { path: '/admin/orders/:id', element: <ProtectedRoute allowedRoles={['admin', 'super_admin']}><AdminOrderDetail /></ProtectedRoute> },
  { path: '/admin/messages', element: <ProtectedRoute allowedRoles={['admin', 'super_admin']}><AdminMessages /></ProtectedRoute> },
  { path: '/admin/messages/:id', element: <ProtectedRoute allowedRoles={['admin', 'super_admin']}><Conversation /></ProtectedRoute> },
  { path: '/admin/reports', element: <ProtectedRoute allowedRoles={['admin', 'super_admin']}><AdminReports /></ProtectedRoute> },
  { path: '/admin/sourcing', element: <ProtectedRoute allowedRoles={['admin', 'super_admin']}><AdminSourcing /></ProtectedRoute> },
  { path: '/admin/notifications', element: <ProtectedRoute allowedRoles={['admin', 'super_admin']}><AdminNotifications /></ProtectedRoute> },
  { path: '/admin/groupages', element: <ProtectedRoute allowedRoles={['admin', 'super_admin']}><AdminGroupages /></ProtectedRoute> },
  { path: '/admin/groupages/:id', element: <ProtectedRoute allowedRoles={['admin', 'super_admin']}><AdminGroupageDetail /></ProtectedRoute> },
  { path: '/super-admin', element: <ProtectedRoute allowedRoles={['super_admin']}><SuperAdminDashboard /></ProtectedRoute> },
  { path: '/super-admin/users', element: <ProtectedRoute allowedRoles={['super_admin']}><SuperAdminUsers /></ProtectedRoute> },
  { path: '/super-admin/reports', element: <ProtectedRoute allowedRoles={['super_admin']}><SuperAdminReports /></ProtectedRoute> },
  { path: '/super-admin/settings', element: <ProtectedRoute allowedRoles={['super_admin']}><SuperAdminSettings /></ProtectedRoute> },
  { path: '*', element: <NotFound /> },
];

let navigateResolver: (navigate: ReturnType<typeof useNavigate>) => void;

declare global {
  interface Window {
    REACT_APP_NAVIGATE: ReturnType<typeof useNavigate>;
  }
}

export const navigatePromise = new Promise<NavigateFunction>((resolve) => {
  navigateResolver = resolve;
});

export function AppRoutes() {
  const element = useRoutes(routes);
  const navigate = useNavigate();
  useEffect(() => {
    window.REACT_APP_NAVIGATE = navigate;
    navigateResolver(window.REACT_APP_NAVIGATE);
  });
  return (
    <Suspense fallback={<PageLoader />}>
      {element}
    </Suspense>
  );
}

export default routes;
